import { HTMLAttributes } from 'react'
import { match } from 'ts-pattern'
import ArrowBack from 'assets/icons/arrow-back.svg'
import ArrowDown from 'assets/icons/arrow-down.svg'
import ArrowLeft from 'assets/icons/arrow-left.svg'
import ArrowRight from 'assets/icons/arrow-right.svg'
import ArrowOutward from 'assets/icons/arrow_outward.svg'
import Bars from 'assets/icons/bars.svg'
import Calendar from 'assets/icons/calendar.svg'
import FavoriteFilled from 'assets/icons/favorite-filled.svg'
import Favorite from 'assets/icons/favorite.svg'
import Mountain from 'assets/icons/mountain.svg'
import Check from 'assets/icons/nucleo/14px_check.svg'
import HourGlass from 'assets/icons/nucleo/18px_hourglass-clock.svg'
import RectArrow from 'assets/icons/nucleo/18px_open-rect-arrow-in.svg'
import Pin from 'assets/icons/pin.svg'
import Run from 'assets/icons/run.svg'
import Share from 'assets/icons/share.svg'
import Cart from 'assets/icons/shopping-cart.svg'
import Time from 'assets/icons/time.svg'
import Times from 'assets/icons/times.svg'
import UserFull from 'assets/icons/user-full.svg'
import UserOutline from 'assets/icons/user.svg'

import {
  getIconFactoryCSS,
  StyledButtonWrapper,
  StyledDivWrapper,
  YellowDot
} from 'components/IconButton.style'
import { Loader, LoaderSize, LoaderVariant } from './common/Loader'

export enum IconButtonSize {
  small = 'small',
  regular = 'regular'
}

export enum IconButtonVariation {
  primary = 'primary',
  whiteOutline = 'whiteOutline',
  blackOutline = 'blackOutline',
  white = 'white',
  greyFull = 'greyFull'
}

interface IconButtonProps extends HTMLAttributes<HTMLElement> {
  icon: IconFactoryValue
  size: IconButtonSize
  isButtonElement?: boolean
  variation?: IconButtonVariation
  isDisabled?: boolean
  countValue?: number
  displayZeroCountValue?: boolean
}

interface ButtonWrapperProps extends HTMLAttributes<HTMLElement> {
  children: React.ReactNode
  isButtonElement?: boolean
  variation?: IconButtonVariation
  size: IconButtonSize
  isDisabled?: boolean
  icon?: string
  countValue?: number
  displayZeroCountValue?: boolean
}
//svg
interface IconFactory extends HTMLAttributes<SVGElement> {
  icon: IconFactoryValue
}

const iconFactoryDictionary = {
  user: UserFull,
  userOutline: UserOutline,
  favorite: Favorite,
  favoriteFilled: FavoriteFilled,
  arrowRight: ArrowRight,
  arrowDown: ArrowDown,
  arrowLeft: ArrowLeft,
  arrowBack: ArrowBack,
  arrowOutward: ArrowOutward,
  bars: Bars,
  times: Times,
  mountain: Mountain,
  time: Time,
  run: Run,
  showList: RectArrow,
  calendar: Calendar,
  check: Check,
  share: Share,
  pin: Pin,
  cart: Cart,
  hourGlass: HourGlass
}

type IconFactoryValue = keyof typeof iconFactoryDictionary | 'progress'

export const IconFactory = ({ icon, ...props }: IconFactory) =>
  match(icon)
    .with('progress', () => (
      <Loader size={LoaderSize.SMALL} variant={LoaderVariant.PRIMARY} />
    ))
    .otherwise((ic) => {
      const Icon = iconFactoryDictionary[ic]
      return <Icon {...props} />
    })

function ButtonWrapper({
  isButtonElement,
  children,
  size,
  variation,
  isDisabled,
  icon,
  countValue,
  displayZeroCountValue = false,
  ...props
}: ButtonWrapperProps) {
  return isButtonElement ? (
    <StyledButtonWrapper
      $size={size}
      $variation={variation}
      $isDisabled={isDisabled}
      aria-label={icon}
      {...props}
    >
      {children}
    </StyledButtonWrapper>
  ) : (
    <StyledDivWrapper
      $size={size}
      $variation={variation}
      $isDisabled={isDisabled}
      {...props}
    >
      {((countValue && countValue !== 0) || displayZeroCountValue) && (
        <YellowDot>{countValue}</YellowDot>
      )}
      {children}
    </StyledDivWrapper>
  )
}

function IconButton({
  isButtonElement = true,
  icon,
  size,
  variation = IconButtonVariation.whiteOutline,
  isDisabled,
  countValue,
  displayZeroCountValue,
  ...props
}: IconButtonProps) {
  return (
    <ButtonWrapper
      isButtonElement={isButtonElement}
      size={size}
      isDisabled={isDisabled}
      variation={variation}
      icon={icon}
      countValue={countValue}
      displayZeroCountValue={displayZeroCountValue}
      {...props}
    >
      <IconFactory
        icon={icon}
        css={getIconFactoryCSS({
          $size: size,
          $variation: variation,
          $isDisabled: isDisabled
        })}
      />
    </ButtonWrapper>
  )
}

export default IconButton
